import React, { useMemo } from 'react';
import { arrayOf, string } from 'prop-types';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useConfiguration } from '../../../context/configurationContext';
import { parse, stringify } from '../../../util/urlHelpers';
import DropdownFilterEnum from '../DropdownFilterEnum/DropdownFilterEnum';
import { createResourceLocatorString } from '../../../util/routes';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import {
  LISTING_FILTERS_CONFIG,
  TYPES_EXCLUDED_FROM_FILTERS,
} from '../../../constants/listing.constants';

import css from './SearchPageStyles.module.css';

export const SEARCH_PARAM_NAME_CATEGORY = 'category';
export const SEARCH_PARAM_NAME_TYPE = 'type';

export const configsByKey = listingFieldsConfig =>
  listingFieldsConfig?.reduce((configs, conf) => ({ ...configs, [conf.key]: conf }), {});

export const getDropdownOptions = (config, items, intl) => {
  return config?.enumOptions
    ?.filter(option => {
      // Handle main categories like 'guitar', 'piano'
      if (config.key === 'category' || config.key === 'type') {
        return items.includes(option.option);
      }

      return false;
    })
    ?.map(option => ({
      ...option,
      value: option.category
        ? `${option.category.replace(/-/g, '/')}/${option.option}`
        : option.option,
      searchString: intl.formatMessage({ id: option.label }),
      label: intl.formatMessage({ id: option.label }),
      group: option.category
        ?.split('-')
        ?.map(category => intl.formatMessage({ id: `General.listing.category.${category}` }))
        ?.join('-'),
    }));
};

const getTypeOptions = intl => {
  return Object.entries(LISTING_FILTERS_CONFIG)
    .map(([category, types]) => {
      return types.map(type => ({
        option: type,
        group: intl.formatMessage({ id: `General.listing.category.${category}` }),
        category: category,
        label: intl.formatMessage({ id: `General.listing.type.${category}.${type}` }),
        group: intl.formatMessage({ id: `General.listing.category.${category}` }),
        value: `${category}/${type}`,
        searchString: intl.formatMessage({ id: `General.listing.type.${category}.${type}` }),
      }));
    })
    .flat();
};

const CategoryFilter = props => {
  const { categoryOptions } = props;

  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const { search, pathname, state } = location;

  const { category: categoryValue = [] } = parse(search);
  const category = Array.isArray(categoryValue) ? categoryValue : [categoryValue];

  const listingFields = config?.listing?.listingFields;
  const fieldConfigs = configsByKey(listingFields);

  const categoryDropdownOptions = useMemo(
    () => getDropdownOptions(fieldConfigs?.category, categoryOptions, intl),
    [categoryOptions]
  );

  const typeDropdownOptions = useMemo(
    () =>
      getTypeOptions(intl)
        ?.filter(option => {
          const excludedTypesFromFilter = TYPES_EXCLUDED_FROM_FILTERS;
          return !excludedTypesFromFilter?.some(
            type => type.type === option.option && type.category === option.category
          );
        })
        .filter(option => !category?.length || category?.includes(option.category)),
    [intl, category]
  );

  const handleTypeChanged = (values, search) => {
    const categories = new Set(values.map(value => value.split('/')[0]));

    const searchString = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      {
        ...parse(search),
        category: [...new Set([...categories, ...category])],
      }
    );
    history.replace(searchString, state);
  };

  const handleCategoryReset = () => {
    const searchString = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      {
        ...parse(search),
        category: [],
        type: [],
      }
    );
    history.replace(searchString, state);
  };

  return (
    <>
      <DropdownFilterEnum
        options={categoryDropdownOptions}
        searchParamName={SEARCH_PARAM_NAME_CATEGORY}
        placeholder={intl.formatMessage({ id: 'Global.category' })}
        dropdownInputClassName={css.dropdownLabel}
        onValueReset={handleCategoryReset}
      />
      <DropdownFilterEnum
        options={typeDropdownOptions}
        searchParamName={SEARCH_PARAM_NAME_TYPE}
        placeholder={intl.formatMessage({ id: 'Global.type' })}
        dropdownInputClassName={css.dropdownLabel}
        onChange={handleTypeChanged}
      />
    </>
  );
};

CategoryFilter.propTypes = {
  categoryOptions: arrayOf(string).isRequired,
};

export default CategoryFilter;
